import React from 'react';
import { useTranslation } from 'react-i18next';
import { camelize, capitalizeFirstLetter } from 'src/utils/stringFormatter';

import type { Event } from 'src/models/event/_types';

interface EventDescriptionProps {
  event: Event;
}

const BLOCKER_EVENT_TRANSLATION_KEYS: Record<string, string> = {
  'Assessment / profile field block override for check-in':
    'events.assessmentProfileFieldBlockOverrideForCheckIn',
};

export const EventDescription: React.FC<EventDescriptionProps> = ({
  event,
}) => {
  const { t, i18n } = useTranslation();

  const formattedOnBehalfOf =
    event.user !== event.onBehalfOf && event.onBehalfOf
      ? ` for ${event.onBehalfOf}`
      : '';

  const sessionTime =
    event.properties.appointmentDate && event.properties.appointmentTime
      ? t('events.sessionTime', { properties: event.properties })
      : '';

  const displayKey = event.properties.displayKey
    ? capitalizeFirstLetter(event.properties.displayKey)
    : '';

  // Format position for waitlist (1st, 2nd, 3rd, etc.)
  const formattedPosition = (() => {
    const position = event.properties.position;
    if (!position) return '';

    if (position === 1) return 'as 1st';
    if (position === 2) return 'as 2nd';
    if (position === 3) return 'as 3rd';
    return `as ${position}th`;
  })();

  // Format name with quotes
  const formattedName = event.properties.name
    ? `for "${event.properties.name}"`
    : '';

  // Format visit status
  const formattedVisitStatus = (() => {
    let visitStatus = event.properties.visitStatus;
    if (!visitStatus) return '';

    visitStatus = visitStatus === 'noshowed' ? 'no showed' : visitStatus;
    visitStatus = visitStatus.replace(/_/g, ' ');
    return `to "${visitStatus}"`;
  })();

  // Format reminder information
  const formattedReminder = (() => {
    const reminderName = event.properties.reminderName;
    const formattedName =
      (reminderName || '').length > 0 ? `"${reminderName}"` : '';

    const reminderType = (event.properties.reminderType || '').replace(
      /[_-]/g,
      ' '
    );
    return `${reminderType} reminder ${formattedName}`;
  })();

  const formattedTrainerNames = (() => {
    const trainerNames = event.properties.trainerNames;
    const oldTrainerNames = event.properties.oldTrainerNames;
    if (!trainerNames) return '';
    return `to ${trainerNames} from ${oldTrainerNames}`;
  })();

  const formattedDateChanged = (() => {
    const oldAppointmentTime = event.properties.oldAppointmentTime;
    if (!oldAppointmentTime) return '';
    return t('events.updatedSessionTime', {
      properties: event.properties,
    });
  })();

  // Format payout parameters
  const payoutParams = (() => {
    if (!event.properties.payoutParams) return '';

    return Object.entries(event.properties.payoutParams)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  })();

  // Format payout information
  const payout = (() => {
    if (!event.properties.payout) return '';

    const { totalPayout, fee, payout } = event.properties;
    if (!totalPayout || !fee || !payout) return '';

    return [
      `Total Amount: ${totalPayout}`,
      `Fee: ${fee}`,
      `Method: '${payout.method}'`,
      `Destination: '${payout.destination}'`,
    ].join(', ');
  })();

  // Format billing cycle reset for resumed subscription
  const formattedBillingCycleReset = (() => {
    switch (String(event.properties.resetBillingCycleAnchor)) {
      case 'true':
        return t('events.billingCycleReset');
      case 'false':
        return t('events.billingCycleNotReset');
      default:
        return '';
    }
  })();

  // Format prorate behavior for resumed subscription
  const formattedProrateBehavior = (() => {
    switch (event.properties.resumeProrateBehavior) {
      case 'no_charge':
        return t('events.prorateBehaviorNoCharge');
      case 'no_prorate':
        return t('events.prorateBehaviorNoProrate');
      case 'credit_unused_time':
        return t('events.prorateBehaviorCreditUnusedTime');
      default:
        return '';
    }
  })();

  const getEventDescription = () => {
    const eventKey = camelize(event.name);
    let translationKey =
      BLOCKER_EVENT_TRANSLATION_KEYS[event.name] ??
      `events.${eventKey}${displayKey}`;
    if (!i18n.exists(translationKey)) translationKey = `events.${eventKey}`;

    if (event.properties.trainerNames) {
      return t('events.updatedSessionStaff', {
        event: event,
        properties: event.properties,
        formattedTrainerNames,
      });
    } else if (event.properties.oldAppointmentTime) {
      return t('events.updatedSessionTime', {
        event: event,
        properties: event.properties,
        formattedDateChanged,
      });
    } else if (event.properties.visitStatus) {
      return `${event.user} ${event.name} ${formattedVisitStatus} ${formattedOnBehalfOf} ${sessionTime}`;
    } else if (i18n.exists(translationKey)) {
      return t(translationKey, {
        event: event.name,
        properties: event.properties,
        onBehalfOf: event.onBehalfOf,
        user: event.user,
        formattedOnBehalfOf,
        sessionTime,
        formattedPosition,
        formattedName,
        formattedVisitStatus,
        formattedReminder,
        formattedTrainerNames,
        formattedDateChanged,
        formattedBillingCycleReset,
        formattedProrateBehavior,
        reason: event.properties.reason ?? event.properties.blockReason,
        payoutParams,
        payout,
        purchase_link: event.properties.purchase_link,
      });
    } else if (event.properties.name) {
      return `${event.name} "${event.properties.name}" ${formattedOnBehalfOf}`;
    } else {
      return `${event.name} ${formattedOnBehalfOf}`;
    }
  };

  return <>{getEventDescription()}</>;
};
